import React, { useState } from 'react';
import { BookOpen, Copy, Search } from 'lucide-react';

const RegexLibraryTool = () => {
  const [search, setSearch] = useState('');
  const [testText, setTestText] = useState('');
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [copied, setCopied] = useState('');

  const patterns = [
    {
      category: 'Email & Contact',
      items: [
        { name: 'Email Address', pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$', desc: 'Validates standard email addresses', example: 'user@example.com' },
        { name: 'Phone (US)', pattern: '^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$', desc: 'US phone number formats', example: '(123) 456-7890' },
        { name: 'Phone (International)', pattern: '^\\+?[1-9]\\d{1,14}$', desc: 'E.164 international format', example: '+14155552671' },
      ]
    },
    {
      category: 'Web & URLs',
      items: [
        { name: 'URL', pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)', desc: 'HTTP/HTTPS URLs', example: 'https://example.com/path' },
        { name: 'Domain Name', pattern: '^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}$', desc: 'Valid domain names', example: 'sub.example.com' },
        { name: 'IP Address (IPv4)', pattern: '^((25[0-5]|(2[0-4]|1\\d|[1-9]|)\\d)\\.?\\b){4}$', desc: 'IPv4 addresses', example: '192.168.1.1' },
        { name: 'Slug', pattern: '^[a-z0-9]+(?:-[a-z0-9]+)*$', desc: 'URL-friendly slugs', example: 'my-blog-post' },
      ]
    },
    {
      category: 'Dates & Times',
      items: [
        { name: 'Date (YYYY-MM-DD)', pattern: '^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])$', desc: 'ISO date format', example: '2024-03-15' },
        { name: 'Date (MM/DD/YYYY)', pattern: '^(0[1-9]|1[0-2])\\/(0[1-9]|[12]\\d|3[01])\\/\\d{4}$', desc: 'US date format', example: '03/15/2024' },
        { name: 'Time (24h)', pattern: '^([01]\\d|2[0-3]):([0-5]\\d)$', desc: '24-hour time format', example: '14:30' },
        { name: 'Time (12h)', pattern: '^(0?[1-9]|1[0-2]):[0-5]\\d\\s?(AM|PM|am|pm)$', desc: '12-hour time with AM/PM', example: '2:30 PM' },
      ]
    },
    {
      category: 'Numbers & Currency',
      items: [
        { name: 'Integer', pattern: '^-?\\d+$', desc: 'Positive or negative integers', example: '-123' },
        { name: 'Decimal', pattern: '^-?\\d*\\.?\\d+$', desc: 'Decimal numbers', example: '3.14159' },
        { name: 'Currency (USD)', pattern: '^\\$?\\d{1,3}(,\\d{3})*(\\.\\d{2})?$', desc: 'US currency format', example: '$1,234.56' },
        { name: 'Percentage', pattern: '^\\d+(\\.\\d+)?%$', desc: 'Percentage values', example: '75.5%' },
      ]
    },
    {
      category: 'Security & IDs',
      items: [
        { name: 'Strong Password', pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$', desc: 'Min 8 chars, upper, lower, number, special', example: 'Pass@123' },
        { name: 'UUID v4', pattern: '^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$', desc: 'UUID version 4 format', example: '550e8400-e29b-41d4-a716-446655440000' },
        { name: 'Credit Card', pattern: '^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13})$', desc: 'Visa, Mastercard, Amex', example: '4111111111111111' },
        { name: 'SSN (US)', pattern: '^\\d{3}-\\d{2}-\\d{4}$', desc: 'Social Security Number', example: '123-45-6789' },
      ]
    },
    {
      category: 'Text Validation',
      items: [
        { name: 'Username', pattern: '^[a-zA-Z0-9_]{3,16}$', desc: '3-16 alphanumeric chars or underscore', example: 'john_doe123' },
        { name: 'Alphabetic Only', pattern: '^[a-zA-Z]+$', desc: 'Letters only', example: 'HelloWorld' },
        { name: 'Alphanumeric', pattern: '^[a-zA-Z0-9]+$', desc: 'Letters and numbers only', example: 'abc123' },
        { name: 'No Special Chars', pattern: '^[a-zA-Z0-9 ]+$', desc: 'Letters, numbers, spaces', example: 'Hello World 123' },
        { name: 'Hex Color', pattern: '^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$', desc: 'Hex color codes', example: '#FF5733' },
      ]
    },
  ];

  const copyPattern = (pattern, name) => {
    navigator.clipboard.writeText(pattern);
    setCopied(name);
    setTimeout(() => setCopied(''), 2000);
  };

  const testPattern = (pattern) => {
    if (!testText) return null;
    try {
      const regex = new RegExp(pattern);
      return regex.test(testText);
    } catch {
      return null;
    }
  };

  const filteredPatterns = patterns.map(cat => ({
    ...cat,
    items: cat.items.filter(item => 
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.desc.toLowerCase().includes(search.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patterns..."
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-6 max-h-[600px] overflow-y-auto pr-2">
          {filteredPatterns.map((category) => (
            <div key={category.category}>
              <h3 className="text-sm font-medium text-blue-400 mb-3">{category.category}</h3>
              <div className="space-y-2">
                {category.items.map((item) => (
                  <div
                    key={item.name}
                    className={`p-3 bg-gray-800 rounded-lg cursor-pointer transition-colors ${
                      selectedPattern?.name === item.name ? 'ring-2 ring-blue-500' : 'hover:bg-gray-750'
                    }`}
                    onClick={() => setSelectedPattern(item)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-white">{item.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyPattern(item.pattern, item.name); }}
                        className={`p-1 rounded transition-colors ${
                          copied === item.name ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">{item.desc}</p>
                    <code className="text-xs text-green-400 block truncate">{item.pattern}</code>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-800 rounded-lg sticky top-0">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Test Pattern</h3>
            <input
              type="text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Enter text to test..."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-blue-500 mb-3"
            />
            
            {selectedPattern && (
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Selected Pattern</div>
                  <div className="font-medium text-white">{selectedPattern.name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Example</div>
                  <code className="text-sm text-blue-400">{selectedPattern.example}</code>
                </div>
                {testText && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Result</div>
                    <div className={`text-sm font-medium ${
                      testPattern(selectedPattern.pattern) ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {testPattern(selectedPattern.pattern) ? '✓ Match' : '✗ No Match'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!selectedPattern && (
              <p className="text-sm text-gray-500">Select a pattern to test</p>
            )}
          </div>

          <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
            <h3 className="text-sm font-medium text-blue-300 mb-2">Tips</h3>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Click a pattern to select it</li>
              <li>• Use the copy button to copy regex</li>
              <li>• Test with your own input</li>
              <li>• Patterns use JavaScript regex syntax</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegexLibraryTool;
